























































































































































import { defineComponent, ref, useFetch, useRoute, onMounted } from '@nuxtjs/composition-api';
import { uniq } from 'lodash-es';
import EventCard from '~/components/pages/Event/EventCard.vue';
import AppArchiveCard from '~/components/partials/App/AppArchiveCard.vue';
import AppArticleCardList from '~/components/partials/App/AppArticleCardList.vue';
import AppContentGrid from '~/components/partials/App/AppContentGrid.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppProducerCard from '~/components/partials/App/AppProducerCard.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import ContentLayout from '~/components/partials/Layout/ContentLayout.vue';
import { useListFavorite } from '~/composables/contents/useListFavorite';
import { useTags } from '~/composables/contents/useTags';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { TAG_SEARCH_DISPLAY_ITEMS_NUM, SORT_TYPE } from '~/constants';
import { nonNullable, contentsSearchParams } from '~/utils';
import { PageError } from '~/utils/error';
import type { ArticleCard, ArticleCategory, EditorProfile, SortedProducer } from '~/types/modules/contents';
import { EventSummaryResponseSchema, EventCardTags } from '~/types/modules/event';
import { ArticleType, FavoriteTypes } from '~/enums';

export default defineComponent({
  components: {
    AppLink,
    AppLoader,
    ContentLayout,
    AppContentGrid,
    AppArchiveCard,
    AppArticleCardList,
    AppProducerCard,
    EventCard,
    AppSeeMoreButton,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const { fetchAllFavorites } = useListFavorite();
    const { allTagNames, findTagsByEventId } = useTags();

    const isLoading = ref(true); // 全体画面のローディングフラグ
    const tagName = ref('');
    const tagId = ref(0);
    const tagPermalinkArray = ref<string[]>([]);

    // 特集関連
    const specialFeatures = ref<ArticleCard[]>([]);
    const specialFeaturesPage = ref(0);
    const isLoadingMoreSpecialFeatures = ref(false); // もっと見るボタンのローディングフラグ
    const hasDisplayedAllSpecialFeatures = ref(false); // 全部表示されているかどうか

    // 読み物関連
    const wineArticles = ref<ArticleCard[]>([]);
    const categories = ref<ArticleCategory[]>([]);
    const authors = ref<EditorProfile[]>([]);
    const articlesPage = ref(0);
    const isLoadingMoreArticles = ref(false);
    const hasDisplayedAllArticles = ref(false);

    // 生産者特集関連
    const sortedProducerArticles = ref<any[]>([]);
    const producersMasterData = ref<SortedProducer[]>([]);
    const producersPage = ref(0);
    const isLoadingMoreProducers = ref(false);
    const hasDisplayedAllProducers = ref(false);

    // イベント関連
    const events = ref<EventSummaryResponseSchema[]>([]);
    const eventsTags = ref<EventCardTags[]>([]);
    const eventsPage = ref(1);
    const isLoadingMoreEvents = ref(false);
    const hasDisplayedAllEvents = ref(false);

    const { fetchState } = useFetch(async () => {
      const tagPermalink = route.value.query.tag;
      // urlにパーマリンクがついてない場合
      if (!tagPermalink) {
        throw new PageError(404, {});
      }
      const { result } = await store.dispatch('modules/content/fetchTag', {
        permalink: tagPermalink.toString(),
      });
      // タグ存在しない場合
      if (!result?.length) {
        throw new PageError(404, {});
      }

      isLoading.value = true;

      tagName.value = result[0].contents.tag_name || '';
      tagId.value = result[0].article_id;
      tagPermalinkArray.value = [tagPermalink.toString()];

      // 各コンテンツを取得
      await fetchSpecialFeatures(tagId.value);
      await fetchArticles(tagId.value);
      try {
        categories.value = await store.dispatch('modules/content/fetchArticleCategories', {});
      } catch {
        // 何にもしない；　他のコンテンツを表示させる
      }
      await fetchProducers(tagId.value);
      await fetchEvents(tagPermalinkArray.value);

      isLoading.value = false;
    });

    /** ↓↓特集PART↓↓ */
    // 特集を取得
    const fetchSpecialFeatures = async (tagId: number) => {
      try {
        const { q } = contentsSearchParams({
          articleType: ArticleType.Archives,
          tagId: tagId.toString(),
        });
        const { articles, total } = await store.dispatch('modules/content/fetchArticles', {
          limit: TAG_SEARCH_DISPLAY_ITEMS_NUM,
          offset: specialFeaturesPage.value * TAG_SEARCH_DISPLAY_ITEMS_NUM,
          q,
          sortIndex: 0, // 特集一覧のデフォルト似合わせる： アーカイブの新着順
          type: SORT_TYPE.ARCHIVE,
        });
        specialFeatures.value = specialFeatures.value.concat(articles || []);
        hasDisplayedAllSpecialFeatures.value =
          total === 0 || total <= TAG_SEARCH_DISPLAY_ITEMS_NUM * (specialFeaturesPage.value + 1);
      } catch {
        // 何にもしない；　他のコンテンツを表示させる
      }
    };

    // 特集を追加で表示する
    const displayAdditionalSpecialFeatures = async () => {
      isLoadingMoreSpecialFeatures.value = true;
      specialFeaturesPage.value++;
      await fetchSpecialFeatures(tagId.value);
      isLoadingMoreSpecialFeatures.value = false;
    };
    /** ↑↑特集PART↑↑ */

    /** ↓↓読み物PART↓↓ */
    // 読み物を取得
    const fetchArticles = async (tagId: number) => {
      try {
        const { q } = contentsSearchParams({
          articleType: ArticleType.Articles,
          tagId: tagId.toString(),
        });
        const { articles, total } = await store.dispatch('modules/content/fetchArticles', {
          limit: TAG_SEARCH_DISPLAY_ITEMS_NUM,
          offset: articlesPage.value * TAG_SEARCH_DISPLAY_ITEMS_NUM,
          q,
          sortIndex: 0, // 読み物一覧のデフォルト似合わせる： 公開日の新着順
        });
        wineArticles.value = wineArticles.value.concat(articles || []);

        const authorIds = wineArticles.value
          .filter((article) => article.authorId)
          .map((article) => Number(article.authorId));
        const filteredAuthorIds = uniq(authorIds);
        authors.value = await store.dispatch('modules/content/fetchAuthors', {
          authorIds: filteredAuthorIds,
        });

        hasDisplayedAllArticles.value = total === 0 || total <= TAG_SEARCH_DISPLAY_ITEMS_NUM * (articlesPage.value + 1);
      } catch {
        // 何にもしない；　他のコンテンツを表示させる
      }
    };

    // 読み物を追加で表示する
    const displayAdditionalArticles = async () => {
      isLoadingMoreArticles.value = true;
      articlesPage.value++;
      await fetchArticles(tagId.value);
      isLoadingMoreArticles.value = false;
    };
    /** ↑↑読み物PART↑↑ */

    /** ↓↓生産者特集PART↓↓ */
    // ソートされた全生産者、タグ関連生産者特集、商品情報を取得
    const fetchProducers = async (tagId: number) => {
      try {
        // サーチ記事マスタの生産者記事からソート後の結果を取得
        const { q } = contentsSearchParams({
          articleType: ArticleType.Producers,
          tagId: tagId.toString(),
        });
        const { articles, total } = await store.dispatch('modules/content/fetchArticles', {
          limit: TAG_SEARCH_DISPLAY_ITEMS_NUM,
          offset: producersPage.value * TAG_SEARCH_DISPLAY_ITEMS_NUM,
          q,
          isSortingProducerNames: true,
        });

        // CMS APIから生産者記事を取得
        const permalinks = articles.map((article) => article.permalink);

        // サーチ生産者マスタから生産者データを取得
        const { producers } = await store.dispatch('modules/content/fetchSortedProducers', {
          // カタカナ順
          sort: 2,
          rows: TAG_SEARCH_DISPLAY_ITEMS_NUM,
          producerIds: permalinks,
        });
        producersMasterData.value = producersMasterData.value.concat(producers || []);

        sortedProducerArticles.value = producers.filter(nonNullable);

        hasDisplayedAllProducers.value =
          total === 0 || total <= TAG_SEARCH_DISPLAY_ITEMS_NUM * (producersPage.value + 1);
      } catch {
        // 何にもしない；　他のコンテンツを表示させる
      }
    };

    // 生産者特集を追加で表示する
    const displayAdditionalProducers = async () => {
      isLoadingMoreProducers.value = true;
      producersPage.value++;
      await fetchProducers(tagId.value);
      isLoadingMoreProducers.value = false;
    };
    /** ↑↑生産者特集PART↑↑ */

    /** ↓↓イベントPART↓↓ */
    // 関連するイベントとタグリストを取得
    const fetchEvents = async (permalink: string[]) => {
      try {
        const q = { tags: permalink, page: eventsPage.value, limit: TAG_SEARCH_DISPLAY_ITEMS_NUM };
        const res = await store.dispatch('event/list/fetchEvents', { query: q });

        events.value = events.value.concat(res.items || []);
        eventsTags.value = eventsTags.value.concat((await allTagNames(res.items)) || []);
        const total = res.totalItems || 0;
        hasDisplayedAllEvents.value = total === 0 || total <= TAG_SEARCH_DISPLAY_ITEMS_NUM * eventsPage.value;
      } catch {
        // 何にもしない；　他のコンテンツを表示させる
      }
    };

    // イベントを追加で表示する
    const displayAdditionalEvents = async () => {
      isLoadingMoreEvents.value = true;
      eventsPage.value++;
      await fetchEvents(tagPermalinkArray.value);
      isLoadingMoreEvents.value = false;
    };
    /** ↑↑イベントPART↑↑ */

    onMounted(() => {
      fetchAllFavorites(FavoriteTypes.ARTICLE);
      fetchAllFavorites(FavoriteTypes.PRODUCER);
    });

    useLayoutProps(() => ({
      layout: {
        headerMenu: 'never', // ContentLayoutの中で設置
      },
      head: {
        title: `「${tagName.value}」に関するコンテンツ一覧`,
        description: 'エノテカ・オンラインのコンテンツ一覧ページです。',
      },
      breadcrumbs: [{ title: 'コンテンツ一覧', to: null }],
      fetchState,
    }));

    return {
      isLoading,
      tagName,
      tagId,
      tagPermalinkArray,
      specialFeatures,
      displayAdditionalSpecialFeatures,
      hasDisplayedAllSpecialFeatures,
      isLoadingMoreSpecialFeatures,
      wineArticles,
      authors,
      categories,
      displayAdditionalArticles,
      hasDisplayedAllArticles,
      isLoadingMoreArticles,
      producersMasterData,
      sortedProducerArticles,
      displayAdditionalProducers,
      hasDisplayedAllProducers,
      isLoadingMoreProducers,
      events,
      eventsTags,
      findTagsByEventId,
      displayAdditionalEvents,
      hasDisplayedAllEvents,
      isLoadingMoreEvents,
    };
  },
});
